import { useEffect, useState } from "react";
import { HelpCircle, Loader2, PlugZap } from "lucide-react";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export const Help = ({ text, testid }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button type="button" data-testid={testid || "help-tooltip"} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
        <HelpCircle className="h-3.5 w-3.5" />O que é isso?
      </button>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs text-xs leading-relaxed">{text}</TooltipContent>
  </Tooltip>
);

export const PageHeader = ({ eyebrow, title, desc, actions }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5 reveal">
    <div>
      {eyebrow && <p className="eyebrow mb-1">{eyebrow}</p>}
      <h1 className="text-xl sm:text-2xl font-semibold">{title}</h1>
      {desc && <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">{desc}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

const Bar = ({ className = "" }) => <div className={`animate-pulse rounded-md bg-secondary ${className}`} />;
export const Loading = ({ label = "Carregando..." }) => (
  <div data-testid="loading-state" aria-label={label} className="space-y-3 py-4"><Bar className="h-5 w-48" /><Bar className="h-24 w-full" /><Bar className="h-24 w-full" /><span className="sr-only">{label}</span></div>
);
export const DashSkeleton = () => (
  <div data-testid="dashboard-skeleton" className="space-y-6"><div className="grid grid-cols-2 lg:grid-cols-6 gap-3">{[...Array(6)].map((_, i) => <Bar key={i} className="h-28" />)}</div><Bar className="h-72" /><Bar className="h-48" /></div>
);

export const Empty = ({ title, desc, action }) => (
  <div data-testid="empty-state" className="surface p-12 text-center">
    <h3 className="font-semibold text-lg">{title}</h3>
    {desc && <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{desc}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const IntegrationPending = ({ title = "Integração pendente", desc, cta = true }) => (
  <div data-testid="integration-pending" className="surface p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center border-dashed">
    <div className="h-12 w-12 rounded-xl bg-[#FBBC04]/15 text-[#E37400] grid place-items-center shrink-0"><PlugZap className="h-6 w-6" /></div>
    <div className="flex-1">
      <p className="eyebrow text-[#E37400] dark:text-[#FDD663]">Configuração necessária</p>
      <h3 className="font-semibold mt-1">{title}</h3>
      <p className="text-sm text-muted-foreground mt-1">{desc || "Conecte e selecione uma conta Google Ads para ver dados reais. Nenhum dado fictício é exibido."}</p>
    </div>
    {cta && <Button asChild data-testid="integration-pending-cta" ><Link to="/app/google-ads">Conectar Google Ads</Link></Button>}
  </div>
);

export function useJob(jobId, onDone) {
  const [job, setJob] = useState(null);
  useEffect(() => {
    if (!jobId) { setJob(null); return; }
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        if (stop) return;
        setJob(data);
        if (data.status === "running") setTimeout(tick, 1500);
        else onDone && onDone(data);
      } catch (_) { if (!stop) setTimeout(tick, 3000); }
    };
    tick();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);
  return job;
}

export const JobProgress = ({ job }) => {
  if (!job || job.status !== "running") return null;
  return (
    <div data-testid="job-progress" className="surface p-5 reveal">
      <div className="flex items-center gap-2 text-sm font-medium"><Loader2 className="h-4 w-4 animate-spin text-primary" />{job.message}</div>
      <Progress value={job.progress} className="mt-3 h-1.5" />
      <p className="text-[11px] text-muted-foreground mt-2">Você pode continuar navegando; a tarefa segue em segundo plano.</p>
    </div>
  );
};
